import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { getSyncEngine, type SyncEngine, type SyncStatus } from './sync';
import { photoPath, queuePhoto, resizeImage, type PhotoPathParts } from './photos';
import { newId, type EnqueueInput } from './outbox';

/**
 * The React surface of the sync engine. Three hooks, and a screen needs
 * nothing else:
 *
 *   const { pending, online, syncing } = useSyncStatus();
 *   const { enqueue } = useOutbox();
 *   const capture = useCapturePhoto({ bucket: 'run-photos' });
 */

export function useSyncStatus(engine?: SyncEngine): SyncStatus {
  const target = engine ?? getSyncEngine();
  return useSyncExternalStore(
    useCallback((listener) => target.subscribe(listener), [target]),
    useCallback(() => target.getSnapshot(), [target]),
    useCallback(() => target.getSnapshot(), [target]),
  );
}

export type UseOutbox = {
  /** Queue a write. Resolves as soon as it is on the phone, not on the island. */
  enqueue: (input: EnqueueInput) => Promise<{ id: string }>;
  /** Ask for a drain now, for a pull to refresh. */
  flush: () => Promise<void>;
  pending: number;
  /** Writes the island refused for good (defect D5). Usually zero. */
  dead: number;
  online: boolean;
  syncing: boolean;
};

export function useOutbox(engine?: SyncEngine): UseOutbox {
  const target = engine ?? getSyncEngine();
  const status = useSyncStatus(target);

  return useMemo(
    () => ({
      enqueue: (input: EnqueueInput) => target.enqueue(input),
      flush: () => target.tick('manual'),
      pending: status.pending,
      dead: status.dead,
      online: status.online,
      syncing: status.syncing,
    }),
    [target, status.pending, status.dead, status.online, status.syncing],
  );
}

export type UseCapturePhotoOptions = {
  bucket: string;
  /** Where the photo belongs, for the path and so the storage policy can read it. */
  parts?: PhotoPathParts;
  maxEdge?: number;
};

export type CaptureResult = { id: string; path: string; bucket: string };

/**
 * Resize, queue, and let the engine upload it. The caller gets the path back
 * straight away and writes it into the row it is about to enqueue, so the tick
 * never waits for the photo.
 */
export function useCapturePhoto(options: UseCapturePhotoOptions, engine?: SyncEngine) {
  const target = engine ?? getSyncEngine();
  const { bucket, parts, maxEdge } = options;

  return useCallback(
    async (file: File, override?: PhotoPathParts): Promise<CaptureResult> => {
      const id = newId();
      const resized = await resizeImage(file, maxEdge);
      const path = photoPath({ ...parts, ...override });
      await queuePhoto({ id, bucket, path, blob: resized });
      void target.tick('enqueue');
      return { id, path, bucket };
    },
    [bucket, maxEdge, parts, target],
  );
}
